import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type {
  Asset,
  BacktestResult,
  BankrollSnapshot,
  BetIdea,
  Candle,
  Contract,
  ContractSide,
  ContractWatch,
  JournalEntry,
  Market,
  ModelPrediction,
  OddsQuote,
  Outcome,
  Position,
  RiskSettings,
  StrategyEvaluation,
  TradeIdea,
} from '../domain/types'
import { createSeedData, type SeedData } from '../data/seed'
import { DEFAULT_SCAN_SETTINGS, type ScanSettings } from '../lib/math/scanner'
import { liquidityScore } from '../lib/math/predictionMarket'
import { uid } from '../lib/id'

export interface ManualContractInput {
  title: string
  category?: string
  contractTitle: string
  yesPriceCents: number
  bidCents?: number
  askCents?: number
  lastCents?: number
  volume?: number
  modelProb?: number
  correlationGroup?: string
  resolutionSource?: string
  resolutionCriteria?: string
  closeTime?: string
  resolutionDate?: string
  mutuallyExclusive?: boolean
}

export interface ContractQuotePatch {
  yesPriceCents?: number
  bidCents?: number
  askCents?: number
  lastCents?: number
  volume?: number
}

export interface EdgeLabActions {
  setRisk: (partial: Partial<RiskSettings>) => void
  setScanSettings: (partial: Partial<ScanSettings>) => void
  acceptDisclaimer: (jurisdiction: string) => void
  setPaperFirst: (value: boolean) => void

  addTradeIdea: (idea: Omit<TradeIdea, 'id' | 'createdAt'>) => string
  updateTradeIdea: (id: string, patch: Partial<TradeIdea>) => void
  removeTradeIdea: (id: string) => void

  addBetIdea: (idea: Omit<BetIdea, 'id' | 'createdAt'>) => string
  updateBetIdea: (id: string, patch: Partial<BetIdea>) => void
  removeBetIdea: (id: string) => void

  addJournalEntry: (entry: Omit<JournalEntry, 'id' | 'createdAt'>) => string
  updateJournalEntry: (id: string, patch: Partial<JournalEntry>) => void
  removeJournalEntry: (id: string) => void

  addPosition: (position: Omit<Position, 'id'>) => string
  closePosition: (id: string, exitPrice: number) => void
  removePosition: (id: string) => void

  addBacktest: (result: BacktestResult) => void
  removeBacktest: (id: string) => void

  saveStrategy: (evaluation: Omit<StrategyEvaluation, 'id' | 'createdAt'>) => string
  updateStrategy: (id: string, patch: Partial<StrategyEvaluation>) => void
  buryStrategy: (id: string, causeOfDeath: string, autopsy: string) => void
  reviveStrategy: (id: string) => void
  removeStrategy: (id: string) => void

  addModelPrediction: (prediction: Omit<ModelPrediction, 'id' | 'createdAt'>) => string
  resolveModelPrediction: (id: string, outcome: 0 | 1) => void

  setContractModelProb: (contractId: string, prob: number | undefined) => void
  updateContractQuote: (contractId: string, patch: ContractQuotePatch) => void
  settleContract: (contractId: string, settlement: 'yes' | 'no', details?: { settledAt?: string; source?: string; notes?: string }) => void
  addManualMarket: (input: ManualContractInput) => string
  addManualContract: (marketId: string, input: Omit<ManualContractInput, 'title' | 'category' | 'mutuallyExclusive'>) => void
  setMarketChecklistAnswer: (marketId: string, itemId: string, done: boolean) => void
  setOddsModelProb: (oddsId: string, prob: number | undefined) => void
  addOddsQuotes: (quotes: Array<Omit<OddsQuote, 'id'>>) => void

  upsertAssetFromCandles: (symbol: string, name: string, candles: Candle[]) => string
  toggleWatchAsset: (assetId: string) => void
  toggleWatchContract: (contractId: string) => void
  upsertContractWatch: (watch: Omit<ContractWatch, 'addedAt' | 'baselineYesCents'> & { baselineYesCents?: number }) => void
  removeContractWatch: (contractId: string, side?: ContractSide) => void

  addBankrollSnapshot: (snapshot: Omit<BankrollSnapshot, 'id'>) => void
  recordOutcome: (outcome: Omit<Outcome, 'id'>) => void

  resetToSeed: () => void
}

export type EdgeLabStore = SeedData & { scanSettings: ScanSettings } & EdgeLabActions

function initialState(): SeedData & { scanSettings: ScanSettings } {
  return { ...createSeedData(), scanSettings: { ...DEFAULT_SCAN_SETTINGS } }
}

function patchById<T extends { id: string }>(list: T[], id: string, patch: Partial<T>): T[] {
  return list.map((item) => (item.id === id ? { ...item, ...patch } : item))
}

function clampCents(value: number, min = 1, max = 99): number {
  if (!Number.isFinite(value)) return min
  return Math.max(min, Math.min(max, Math.round(value)))
}

function createManualContract(
  marketId: string,
  input: Omit<ManualContractInput, 'title' | 'category' | 'mutuallyExclusive'>,
): Contract {
  const last = clampCents(input.lastCents ?? input.yesPriceCents)
  const bid = clampCents(input.bidCents ?? Math.max(1, last - 2), 1, last)
  const ask = clampCents(input.askCents ?? Math.min(99, last + 2), last, 99)
  const volume = Math.max(0, Math.round(input.volume ?? 0))

  return {
    id: uid('ct'),
    marketId,
    title: input.contractTitle || 'YES',
    yesPriceCents: last,
    bidCents: bid,
    askCents: ask,
    lastCents: last,
    volume,
    liquidityScore: liquidityScore(volume, ask - bid),
    modelProb: input.modelProb,
    settlement: null,
    tags: ['manual'],
    priceHistory: [{ time: new Date().toISOString(), yesPriceCents: last, bidCents: bid, askCents: ask }],
    resolutionSource: input.resolutionSource,
    resolutionCriteria: input.resolutionCriteria,
  }
}

function findContract(markets: Market[], contractId: string): Contract | undefined {
  for (const market of markets) {
    const contract = market.contracts.find((c) => c.id === contractId)
    if (contract) return contract
  }
  return undefined
}

export const useStore = create<EdgeLabStore>()(
  persist(
    (set) => ({
      ...initialState(),

      setRisk: (partial) => set((s) => ({ risk: { ...s.risk, ...partial } })),
      setScanSettings: (partial) => set((s) => ({ scanSettings: { ...s.scanSettings, ...partial } })),
      acceptDisclaimer: (jurisdiction) =>
        set((s) => ({ meta: { ...s.meta, acceptedLegalDisclaimer: true, jurisdiction } })),
      setPaperFirst: (value) => set((s) => ({ meta: { ...s.meta, paperFirstMode: value } })),

      addTradeIdea: (idea) => {
        const id = uid('trade')
        set((s) => ({ tradeIdeas: [{ ...idea, id, createdAt: new Date().toISOString() }, ...s.tradeIdeas] }))
        return id
      },
      updateTradeIdea: (id, patch) => set((s) => ({ tradeIdeas: patchById(s.tradeIdeas, id, patch) })),
      removeTradeIdea: (id) => set((s) => ({ tradeIdeas: s.tradeIdeas.filter((t) => t.id !== id) })),

      addBetIdea: (idea) => {
        const id = uid('bet')
        set((s) => ({ betIdeas: [{ ...idea, id, createdAt: new Date().toISOString() }, ...s.betIdeas] }))
        return id
      },
      updateBetIdea: (id, patch) => set((s) => ({ betIdeas: patchById(s.betIdeas, id, patch) })),
      removeBetIdea: (id) => set((s) => ({ betIdeas: s.betIdeas.filter((b) => b.id !== id) })),

      addJournalEntry: (entry) => {
        const id = uid('jr')
        set((s) => ({ journal: [{ ...entry, id, createdAt: new Date().toISOString() }, ...s.journal] }))
        return id
      },
      updateJournalEntry: (id, patch) => set((s) => ({ journal: patchById(s.journal, id, patch) })),
      removeJournalEntry: (id) => set((s) => ({ journal: s.journal.filter((j) => j.id !== id) })),

      addPosition: (position) => {
        const id = uid('pos')
        set((s) => ({ positions: [{ ...position, id }, ...s.positions] }))
        return id
      },
      closePosition: (id, exitPrice) =>
        set((s) => ({
          positions: s.positions.map((p) => {
            if (p.id !== id) return p
            const dir = p.direction === 'long' ? 1 : -1
            const realizedPnl = (exitPrice - p.entryPrice) * p.size * dir
            return { ...p, status: 'closed', closedAt: new Date().toISOString(), currentPrice: exitPrice, realizedPnl }
          }),
        })),
      removePosition: (id) => set((s) => ({ positions: s.positions.filter((p) => p.id !== id) })),

      addBacktest: (result) => set((s) => ({ backtests: [result, ...s.backtests].slice(0, 25) })),
      removeBacktest: (id) => set((s) => ({ backtests: s.backtests.filter((b) => b.id !== id) })),

      saveStrategy: (evaluation) => {
        const id = uid('strat')
        set((s) => ({ strategies: [{ ...evaluation, id, createdAt: new Date().toISOString() }, ...s.strategies].slice(0, 50) }))
        return id
      },
      updateStrategy: (id, patch) => set((s) => ({ strategies: patchById(s.strategies, id, patch) })),
      buryStrategy: (id, causeOfDeath, autopsy) =>
        set((s) => ({ strategies: patchById(s.strategies, id, { status: 'buried', causeOfDeath, autopsy }) })),
      reviveStrategy: (id) =>
        set((s) => ({ strategies: patchById(s.strategies, id, { status: 'candidate', causeOfDeath: undefined, autopsy: undefined }) })),
      removeStrategy: (id) => set((s) => ({ strategies: s.strategies.filter((x) => x.id !== id) })),

      addModelPrediction: (prediction) => {
        const id = uid('mp')
        set((s) => ({ modelPredictions: [{ ...prediction, id, createdAt: new Date().toISOString() }, ...s.modelPredictions] }))
        return id
      },
      resolveModelPrediction: (id, outcome) =>
        set((s) => ({ modelPredictions: patchById(s.modelPredictions, id, { resolved: true, outcome }) })),

      setContractModelProb: (contractId, prob) =>
        set((s) => ({
          markets: s.markets.map((m) => ({
            ...m,
            contracts: m.contracts.map((c) => (c.id === contractId ? { ...c, modelProb: prob } : c)),
          })),
        })),
      updateContractQuote: (contractId, patch) =>
        set((s) => ({
          markets: s.markets.map((m) => ({
            ...m,
            contracts: m.contracts.map((c) => {
              if (c.id !== contractId) return c
              const last = clampCents(patch.lastCents ?? patch.yesPriceCents ?? c.lastCents)
              const bid = clampCents(patch.bidCents ?? c.bidCents, 1, last)
              const ask = clampCents(patch.askCents ?? c.askCents, last, 99)
              const volume = Math.max(0, Math.round(patch.volume ?? c.volume))
              return {
                ...c,
                yesPriceCents: last,
                lastCents: last,
                bidCents: bid,
                askCents: ask,
                volume,
                liquidityScore: liquidityScore(volume, ask - bid),
                priceHistory: [
                  ...((c as { priceHistory?: Contract['priceHistory'] }).priceHistory ?? []),
                  { time: new Date().toISOString(), yesPriceCents: last, bidCents: bid, askCents: ask },
                ].slice(-120),
              }
            }),
          })),
        })),
      settleContract: (contractId, settlement, details) =>
        set((s) => ({
          markets: s.markets.map((m) => ({
            ...m,
            contracts: m.contracts.map((c) =>
              c.id === contractId
                ? {
                    ...c,
                    settlement,
                    settledAt: details?.settledAt ?? new Date().toISOString(),
                    settlementSource: details?.source ?? c.resolutionSource,
                    settlementNotes: details?.notes,
                  }
                : c,
            ),
          })),
        })),
      addManualMarket: (input) => {
        const marketId = uid('mkt')
        set((s) => {
          const market: Market = {
            id: marketId,
            title: input.title,
            category: input.category || 'Manual',
            source: 'manual',
            closeTime: input.closeTime,
            resolutionDate: input.resolutionDate,
            mutuallyExclusive: input.mutuallyExclusive,
            correlationGroup: input.correlationGroup,
            contracts: [createManualContract(marketId, input)],
          }
          return { markets: [market, ...s.markets] }
        })
        return marketId
      },
      addManualContract: (marketId, input) =>
        set((s) => ({
          markets: s.markets.map((m) =>
            m.id === marketId ? { ...m, contracts: [...m.contracts, createManualContract(marketId, input)] } : m,
          ),
        })),
      setMarketChecklistAnswer: (marketId, itemId, done) =>
        set((s) => ({
          markets: s.markets.map((m) =>
            m.id === marketId
              ? { ...m, resolutionChecklist: { ...(m.resolutionChecklist ?? {}), [itemId]: done } }
              : m,
          ),
        })),
      setOddsModelProb: (oddsId, prob) => set((s) => ({ odds: patchById(s.odds, oddsId, { modelProb: prob }) })),
      addOddsQuotes: (quotes) =>
        set((s) => ({ odds: [...quotes.map((q) => ({ ...q, id: uid('odds') })), ...s.odds] })),

      upsertAssetFromCandles: (symbol, name, candles) => {
        const id = `asset-${symbol.toLowerCase()}`
        const asset: Asset = { id, symbol: symbol.toUpperCase(), name, kind: 'stock', currency: 'USD', tags: ['imported'], candles }
        set((s) => {
          const exists = s.assets.some((a) => a.id === id)
          return { assets: exists ? s.assets.map((a) => (a.id === id ? { ...a, candles, name } : a)) : [asset, ...s.assets] }
        })
        return id
      },
      toggleWatchAsset: (assetId) =>
        set((s) => ({
          watchlistAssetIds: s.watchlistAssetIds.includes(assetId)
            ? s.watchlistAssetIds.filter((x) => x !== assetId)
            : [...s.watchlistAssetIds, assetId],
        })),
      toggleWatchContract: (contractId) =>
        set((s) => {
          const active = s.watchlistContractIds.includes(contractId)
          if (active) {
            return {
              watchlistContractIds: s.watchlistContractIds.filter((x) => x !== contractId),
              contractWatches: s.contractWatches.filter((w) => w.contractId !== contractId),
            }
          }
          const contract = findContract(s.markets, contractId)
          const watch: ContractWatch | null = contract
            ? {
                contractId,
                side: 'yes',
                targetPriceCents: contract.askCents,
                note: '',
                addedAt: new Date().toISOString(),
                baselineYesCents: contract.lastCents,
              }
            : null
          return {
            watchlistContractIds: [...s.watchlistContractIds, contractId],
            contractWatches: watch ? [...s.contractWatches, watch] : s.contractWatches,
          }
        }),
      upsertContractWatch: (watch) =>
        set((s) => {
          const contract = findContract(s.markets, watch.contractId)
          const next: ContractWatch = {
            ...watch,
            targetPriceCents: clampCents(watch.targetPriceCents),
            addedAt: new Date().toISOString(),
            baselineYesCents: watch.baselineYesCents ?? contract?.lastCents ?? watch.targetPriceCents,
          }
          const exists = s.contractWatches.some((w) => w.contractId === watch.contractId && w.side === watch.side)
          return {
            watchlistContractIds: s.watchlistContractIds.includes(watch.contractId)
              ? s.watchlistContractIds
              : [...s.watchlistContractIds, watch.contractId],
            contractWatches: exists
              ? s.contractWatches.map((w) =>
                  w.contractId === watch.contractId && w.side === watch.side ? { ...w, ...next, addedAt: w.addedAt } : w,
                )
              : [...s.contractWatches, next],
          }
        }),
      removeContractWatch: (contractId, side) =>
        set((s) => {
          const contractWatches = s.contractWatches.filter((w) => w.contractId !== contractId || (side !== undefined && w.side !== side))
          const stillWatched = contractWatches.some((w) => w.contractId === contractId)
          return {
            contractWatches,
            watchlistContractIds: stillWatched ? s.watchlistContractIds : s.watchlistContractIds.filter((x) => x !== contractId),
          }
        }),

      addBankrollSnapshot: (snapshot) =>
        set((s) => ({ bankrollHistory: [...s.bankrollHistory, { ...snapshot, id: uid('bk') }] })),
      recordOutcome: (outcome) => set((s) => ({ outcomes: [{ ...outcome, id: uid('oc') }, ...s.outcomes] })),

      resetToSeed: () => set(() => initialState()),
    }),
    {
      name: 'edgelab-store-v1',
      version: 1,
    },
  ),
)
